import { useAuthorization } from '@shared/auth/use-authorization';
import { isEmpty } from 'lodash';
import { useState } from 'react';
import {
  HiOutlineCheck,
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineXMark,
} from 'react-icons/hi2';
import { LiaPillsSolid } from 'react-icons/lia';
import { useOutletContext } from 'react-router-dom';

import { Button } from '@/components-new/button';
import { Combobox } from '@/components-new/combobox';
import { EmptyState, EmptyStateBody, EmptyStateHeading } from '@/components-new/empty-state';
import { Field, Label } from '@/components-new/fieldset';
import { Link } from '@/components-new/link';
import { SectionDescription, SectionHeading } from '@/components-new/section';
import {
  useAddDrugToClassification,
  useRemoveDrugFromClassification,
} from '@/features/classifications/api';
import { Classification } from '@/features/classifications/types/classification';
import { useDrugsQuery } from '@/features/drugs/api';
import { DrugBase } from '@/features/drugs/types/drug';
import { useParams } from '@/router';

type OutletContext = { classification: Classification | undefined };

const AddDrugRow = ({
  classificationId,
  existingDrugIds,
  onDone,
}: {
  classificationId: number;
  existingDrugIds: number[];
  onDone: () => void;
}) => {
  const [selectedDrug, setSelectedDrug] = useState<DrugBase | undefined>();
  const [query, setQuery] = useState('');

  const { data: drugs = [], isFetching } = useDrugsQuery({
    filters: { name: query || undefined },
    queryConfig: { enabled: query.length > 1 },
  });

  const options = drugs.filter(d => !existingDrugIds.includes(d.id));
  const { mutate: addDrug, isPending } = useAddDrugToClassification();

  const handleConfirm = () => {
    if (!selectedDrug) return;
    addDrug({ classificationId, drugId: selectedDrug.id }, { onSuccess: onDone });
  };

  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-zinc-50 border-t border-zinc-100">
      <Field className="flex-1">
        <Label className="sr-only">Search drugs</Label>
        <Combobox
          options={isFetching ? [] : options}
          value={selectedDrug}
          onChange={setSelectedDrug}
          onQueryChange={(q) => setQuery(q ?? '')}
          valueKey="id"
          labelKey="name"
          placeholder="Type to search drugs..."
        />
      </Field>
      <Button color="secondary" disabled={!selectedDrug || isPending} onClick={handleConfirm}>
        <HiOutlineCheck />
        Add
      </Button>
      <Button plain type="button" onClick={onDone} disabled={isPending}>
        <HiOutlineXMark />
      </Button>
    </div>
  );
};

const DrugRow = ({
  drug,
  classificationId,
  canManage,
}: {
  drug: DrugBase;
  classificationId: number;
  canManage: boolean;
}) => {
  const { mutate: removeDrug, isPending } = useRemoveDrugFromClassification();

  return (
    <div className="flex items-center justify-between px-4 py-3">
      <div>
        <Link
          href="/drugs/:id"
          params={{ id: drug.id.toString() }}
          className="text-sm font-medium text-zinc-900 hover:text-teal-700"
        >
          {drug.name}
        </Link>
        {drug.genericName && (
          <p className="text-sm text-zinc-500">{drug.genericName}</p>
        )}
      </div>
      {canManage && (
        <Button
          plain
          type="button"
          disabled={isPending}
          aria-label={`Remove ${drug.name} from classification`}
          onClick={() => removeDrug({ classificationId, drugId: drug.id })}
        >
          <HiOutlineTrash />
        </Button>
      )}
    </div>
  );
};

const ClassificationDrugsPage = () => {
  const { id } = useParams('/classifications/:id');
  const classificationId = parseInt(id, 10);

  const { checkPolicies } = useAuthorization();
  const canManageClassifications = checkPolicies(['canManageClassifications']);

  const { classification } = useOutletContext<OutletContext>();
  const [addingDrug, setAddingDrug] = useState(false);

  const drugs = classification?.drugs ?? [];
  const hasDrugs = !isEmpty(drugs);

  return (
    <div className="mt-4 max-w-2xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <SectionHeading level={3}>Drugs</SectionHeading>
          <SectionDescription>
            Drugs assigned to this classification.
          </SectionDescription>
        </div>
        {canManageClassifications && !addingDrug && hasDrugs && (
          <Button plain onClick={() => setAddingDrug(true)}>
            <HiOutlinePlus />
            Add Drug
          </Button>
        )}
      </div>

      {hasDrugs || addingDrug ? (
        <div className="divide-y divide-zinc-100 rounded-xl border border-zinc-200 bg-white">
          {drugs.map(drug => (
            <DrugRow
              key={drug.id}
              drug={drug}
              classificationId={classificationId}
              canManage={canManageClassifications}
            />
          ))}
          {addingDrug && (
            <AddDrugRow
              classificationId={classificationId}
              existingDrugIds={drugs.map(d => d.id)}
              onDone={() => setAddingDrug(false)}
            />
          )}
        </div>
      ) : (
        <div className="flex h-48 flex-col items-center justify-center gap-4 rounded-xl border border-zinc-200 bg-white">
          <EmptyState>
            <LiaPillsSolid className="size-10 text-zinc-300" />
            <EmptyStateHeading>No drugs yet</EmptyStateHeading>
            <EmptyStateBody>Assign drugs to this classification to get started.</EmptyStateBody>
          </EmptyState>
          {canManageClassifications && (
            <Button color="secondary" onClick={() => setAddingDrug(true)}>
              <HiOutlinePlus />
              Add Drug
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ClassificationDrugsPage;
